import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Upload, 
  Button, 
  Card, 
  message,
  Form,
  InputNumber,
  Select,
  Switch,
  Slider,
  Progress,
  Row,
  Col,
  Space,
  Modal} from 'antd';
import { 
  DownloadOutlined, 
  CheckCircleOutlined,
  FullscreenOutlined,
  InboxOutlined,
  ArrowLeftOutlined} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { 
  resizeImage, 
  ResizeOptions,
  getResizeTypeInfo,
  getAvailableResizeTypes,
  ResizeType,
  ImageFormat,
  getAvailableResizeFormats
} from '../services/imageService';
import { useLanguage } from '../i18n/LanguageContext';

const { Title, Paragraph } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

// Color palette for resize types
const resizeColorPalette = {
  'exact': '#2196f3',    // Blue
  'width': '#4caf50',    // Green
  'height': '#ff9800',   // Orange
  'percentage': '#9c27b0' // Purple
};

interface ResizeParams {
  [key: string]: string | undefined;
  type?: string;
}

const ResizePage: React.FC = () => {
  const params = useParams<ResizeParams>();
  const type = params.type;
  const navigate = useNavigate();

  const { t } = useLanguage();
  
  // Use ref to track if initial setup has happened
  const initializedRef = useRef(false);
  
  const [resizeType, setResizeType] = useState<ResizeType>('exact');
  const [resizeInfo, setResizeInfo] = useState<{
    name: string;
    description: string;
    acceptFormats: string[];
  }>({
    name: t('resize.exact.dimensions'),
    description: t('resize.exact.description'),
    acceptFormats: ['image/jpeg', 'image/png', 'image/webp']
  });
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{ width: number, height: number } | null>(null);
  const [resizedImage, setResizedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageColor, setPageColor] = useState<string>('#2196f3');
  const [progressPercent, setProgressPercent] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  
  // Form values
  const [form] = Form.useForm();
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [targetFormat, setTargetFormat] = useState<ImageFormat | 'original'>('original');
  
  // Get available resize types
  const resizeTypes = getAvailableResizeTypes(t);
  const resizeFormats = getAvailableResizeFormats(t);
  
  // Use ref to store image object URL for cleanup
  const imageObjectUrl = useRef<string | null>(null);
  
  useEffect(() => {
    // Skip if already initialized or no type parameter
    if (initializedRef.current || !type) return;
    
    initializedRef.current = true;
    
    // First check if this is a format-specific resize type (jpg-resize, png-resize, etc.)
    const formatType = resizeFormats.find(format => format.type === type);
    if (formatType) {
      // Set target format based on the resize type
      setTargetFormat(formatType.outputFormat as ImageFormat);
      
      // Set page color based on format
      const colorMap: Record<string, string> = {
        'jpg-resize': '#2196f3',
        'jpeg-resize': '#4caf50',
        'png-resize': '#ff9800'
      };
      setPageColor(colorMap[type] || '#2196f3');
      
      // Set resize method to exact by default
      setResizeType('exact');
      const resizeOption = resizeTypes.find(r => r.type === 'exact');
      if (resizeOption) {
        setResizeInfo({
          name: formatType.name,
          description: formatType.description,
          acceptFormats: [formatType.acceptFormat]
        });
      }
      
      // Set default dimensions
      form.setFieldsValue({ width: 800, height: 600 });
    } else {
      // Check if it's a resize method type (exact, width, height, percentage)
      const resizeOption = resizeTypes.find(r => r.type === type);
      
      if (resizeOption) {
        setResizeType(type as ResizeType);
        setResizeInfo({
          name: resizeOption.name,
          description: resizeOption.description,
          acceptFormats: resizeOption.acceptFormats
        });
        
        // Set color based on resize type
        setPageColor(resizeColorPalette[type as ResizeType] || '#2196f3');
        
        // Set initial form values based on resize type
        form.resetFields();
        
        // Set default values based on resize type
        switch (type) {
          case 'exact':
            form.setFieldsValue({ width: 800, height: 600 });
            break;
          case 'width':
            form.setFieldsValue({ width: 800 });
            break;
          case 'height':
            form.setFieldsValue({ height: 600 });
            break;
          case 'percentage':
            form.setFieldsValue({ percentage: 50 });
            break;
        }
      } else {
        // Invalid resize type - redirect to home
        navigate('/');
      }
    }
  }, [type, navigate, form, resizeTypes, resizeFormats]);
  
  // Simulate resizing progress
  const simulateProgress = () => {
    setIsResizing(true);
    setProgressPercent(0);
    
    const interval = setInterval(() => {
      setProgressPercent(prev => {
        if (prev >= 95) {
          clearInterval(interval);
          return 95;
        }
        return prev + 5;
      });
    }, 50);
    
    return () => clearInterval(interval);
  };
  
  // Get image dimensions when file is selected
  const getImageDimensions = (file: File): Promise<{ width: number, height: number }> => {
    return new Promise((resolve, reject) => {
      try {
        // Use FileReader instead of URL.createObjectURL for more reliability
        const reader = new FileReader();
        
        reader.onload = (event) => {
          try {
            const img = document.createElement('img');
            
            img.onload = () => {
              // Successfully got dimensions
              resolve({ width: img.width, height: img.height });
            };
            
            img.onerror = () => {
              console.error('Image failed to load for dimension calculation');
              // Use fallback dimensions instead of rejecting
              resolve({ width: 800, height: 600 });
            };
            
            // Set image source from FileReader result
            if (event.target && typeof event.target.result === 'string') {
              img.src = event.target.result;
            } else {
              throw new Error('FileReader did not produce a valid result');
            }
          } catch (imgError) {
            console.error('Error setting up image:', imgError);
            // Provide fallback dimensions instead of rejecting
            resolve({ width: 800, height: 600 });
          }
        };
        
        reader.onerror = (fileError) => {
          console.error('Error reading file:', fileError);
          // Provide fallback dimensions instead of rejecting
          resolve({ width: 800, height: 600 });
        };
        
        // Read the file as data URL
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error in getImageDimensions:', error);
        // Provide fallback dimensions instead of rejecting
        resolve({ width: 800, height: 600 });
      }
    });
  };
  
  const handleResize = async () => {
    if (!imageFile) {
      message.error('Please upload an image first!');
      return;
    }
    
    try {
      // Validate form
      await form.validateFields();
      
      setLoading(true);
      const stopProgressSimulation = simulateProgress();
      
      // Get form values
      const formValues = form.getFieldsValue();
      
      // Prepare resize options
      const options: ResizeOptions = {
        type: resizeType,
        maintainAspectRatio
      };
      
      // Add specific options based on resize type
      switch (resizeType) {
        case 'exact':
          options.width = formValues.width;
          options.height = formValues.height;
          break;
        case 'width':
          options.width = formValues.width;
          break;
        case 'height':
          options.height = formValues.height;
          break;
        case 'percentage':
          options.percentage = formValues.percentage;
          break;
      }
      
      // Add format option if not original
      if (targetFormat !== 'original') {
        options.format = targetFormat;
      }
      
      // Resize image
      const result = await resizeImage(imageFile, options);
      setResizedImage(result);
      
      // Complete progress animation
      setProgressPercent(100);
      setTimeout(() => {
        setIsResizing(false);
      }, 500);
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Remove automatic download - let user click the button instead
      // downloadResizedImage(result);
    } catch (error) {
      message.error('Failed to resize image. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const downloadResizedImage = (imageData: string) => {
    const extension = targetFormat !== 'original' 
      ? targetFormat 
      : imageFile?.type.split('/')[1] || 'jpg';
      
    const link = document.createElement('a');
    link.href = imageData;
    link.download = `resized-image.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: resizeInfo.acceptFormats.join(','),
    maxCount: 1,
    showUploadList: true,
    beforeUpload: async (file) => {
      try {
        // Validate file type and size
        if (!file || !file.type) {
          message.error('Invalid file format. Please try another file.');
          return Upload.LIST_IGNORE;
        }
        
        // Check if the file type is supported
        const isAcceptedFormat = resizeInfo.acceptFormats.some(format => 
          file.type.toLowerCase().includes(format.split('/')[1].toLowerCase())
        );
        
        if (!isAcceptedFormat) {
          message.error(`Only ${resizeInfo.acceptFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')} files are accepted for resizing!`);
          return Upload.LIST_IGNORE;
        }
        
        // Get image dimensions - no longer throws errors, now returns fallback values
        const dimensions = await getImageDimensions(file);
        setOriginalDimensions(dimensions);
        
        // Update form defaults based on original dimensions
        const formUpdates: any = {};
        
        if (resizeType === 'exact') {
          formUpdates.width = dimensions.width;
          formUpdates.height = dimensions.height;
        } else if (resizeType === 'width') {
          formUpdates.width = dimensions.width;
        } else if (resizeType === 'height') {
          formUpdates.height = dimensions.height;
        }
        
        // Batch form updates to prevent cascading renders
        form.setFieldsValue(formUpdates);
        
        // Set the file for resizing (do this last to avoid render cycles)
        setImageFile(file);
        
        message.success(`${file.name} uploaded successfully. You can now adjust resize settings.`);
      } catch (error) {
        console.error('Error in beforeUpload handler:', error);
        message.error('Failed to process the image. Please try another file.');
      }
      
      // Prevent actual upload
      return false;
    },
    onChange: (info) => {
      try {
        const { status } = info.file;
        if (status === 'removed') {
          setImageFile(null);
          setResizedImage(null);
          setOriginalDimensions(null);
        }
      } catch (error) {
        console.error('Error in upload onChange handler:', error);
      }
    }
  };
  
  // Calculate aspect ratio for connected width/height inputs
  const updateConnectedDimension = (field: 'width' | 'height', value: number) => {
    if (!maintainAspectRatio || !originalDimensions || !value) return;
    
    const aspectRatio = originalDimensions.width / originalDimensions.height;
    
    if (field === 'width') {
      const newHeight = Math.round(value / aspectRatio);
      form.setFieldsValue({ height: newHeight });
    } else {
      const newWidth = Math.round(value * aspectRatio);
      form.setFieldsValue({ width: newWidth });
    }
  };
  
  // Format dimensions for display
  const formatDimensions = (width: number, height: number): string => {
    return `${width} × ${height} px`;
  };
  
  // Get title based on format type
  const getPageTitle = () => {
    if (type && resizeFormats.find(format => format.type === type)) {
      const formatType = resizeFormats.find(format => format.type === type);
      return formatType ? formatType.name : 'Resize Your Image';
    }
    return 'Resize Your Image';
  };
  
  // Clean up any object URLs on unmount
  useEffect(() => {
    return () => {
      if (imageObjectUrl.current) {
        URL.revokeObjectURL(imageObjectUrl.current);
        imageObjectUrl.current = null;
      }
    };
  }, []);
  
  // Generate image preview URL safely
  const getImagePreviewUrl = (file: File | null): string => {
    if (!file) {
      return '';
    }
    
    try {
      // Simple and safe - don't use refs here
      return URL.createObjectURL(file);
    } catch (error) {
      console.error('Error creating object URL for preview:', error);
      // Return placeholder image
      return '';
    }
  };
  
  return (
    <div className="container">
      <Button 
        type="link" 
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/')}
        style={{ marginBottom: 16, paddingLeft: 0 }}
      >
        {t('compress.back.home')}
      </Button>
      
      <Title level={2} style={{ color: pageColor }}>{getPageTitle()}</Title>
      <Paragraph>
        {getResizeTypeInfo(resizeType, t)}
      </Paragraph>
      
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <div className="upload-section">
            <Title level={3}>{t('resize.upload.title')}</Title>
            <Dragger 
              {...uploadProps} 
              className="upload-area"
              style={{ 
                borderColor: pageColor,
                borderWidth: '1px',
                borderStyle: 'dashed'
              }}
            >
              {imageFile ? (
                <div>
                  <p className="ant-upload-drag-icon">
                    <CheckCircleOutlined style={{ color: pageColor, fontSize: 48 }} />
                  </p>
                  <p className="ant-upload-text" style={{ color: pageColor, fontWeight: 'bold' }}>
                    {t('compress.upload.complete').replace('{filename}', imageFile.name)}
                  </p>
                  {originalDimensions && (
                    <p className="ant-upload-hint">
                      {`${t('resize.original.dimensions')}`} <strong>${formatDimensions(originalDimensions.width, originalDimensions.height)}</strong>
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: pageColor, fontSize: 48 }} />
                  </p>
                  <p className="ant-upload-text">{t('compress.upload.hint').replace('{format}', '')}</p>
                  <p className="ant-upload-hint">
                    {t('resize.supported.formats')} {resizeInfo.acceptFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}
                  </p>
                </div>
              )}
            </Dragger>
          </div>
          
          {imageFile && (
            <Card title={t('resize.options.title')} style={{ marginTop: 24 }}>
              <Form form={form} layout="vertical">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item label={t('resize.method.label')}>
                    <Select
                      value={resizeType}
                      onChange={(value) => {
                        // Only update if value is different to prevent unnecessary renders
                        if (value !== resizeType) {
                          setResizeType(value);
                          // Update page color
                          setPageColor(resizeColorPalette[value] || '#2196f3');
                        }
                      }}
                      style={{ width: '100%' }}
                    >
                      {resizeTypes.map(rt => (
                        <Option key={rt.type} value={rt.type}>{rt.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  
                  {resizeType === 'exact' && (
                    <>
                      <Form.Item 
                        label={t('resize.width.label')} 
                        name="width"
                        rules={[{ required: true, message: 'Please enter width' }]}
                      >
                        <InputNumber 
                          min={1} 
                          max={10000} 
                          style={{ width: '100%' }} 
                          onChange={(value) => updateConnectedDimension('width', value as number)}
                        />
                      </Form.Item>
                      
                      <Form.Item 
                        label={t('resize.height.label')} 
                        name="height"
                        rules={[{ required: true, message: 'Please enter height' }]}
                      >
                        <InputNumber 
                          min={1} 
                          max={10000} 
                          style={{ width: '100%' }} 
                          onChange={(value) => updateConnectedDimension('height', value as number)}
                        />
                      </Form.Item>
                      
                      <Form.Item label={t('resize.aspect.ratio')}>
                        <Switch 
                          checked={maintainAspectRatio}
                          onChange={setMaintainAspectRatio}
                        />
                      </Form.Item>
                    </>
                  )}
                  
                  {resizeType === 'width' && (
                    <Form.Item 
                      label={t('resize.width.label')} 
                      name="width"
                      rules={[{ required: true, message: 'Please enter width' }]}
                    >
                      <InputNumber 
                        min={1} 
                        max={10000} 
                        style={{ width: '100%' }} 
                      />
                    </Form.Item>
                  )}
                  
                  {resizeType === 'height' && (
                    <Form.Item 
                      label={t('resize.height.label')} 
                      name="height"
                      rules={[{ required: true, message: 'Please enter height' }]}
                    >
                      <InputNumber 
                        min={1} 
                        max={10000} 
                        style={{ width: '100%' }} 
                      />
                    </Form.Item>
                  )}
                  
                  {resizeType === 'percentage' && (
                    <Form.Item 
                      label={t('resize.scale.percentage')} 
                      name="percentage"
                      rules={[{ required: true, message: t('resize.percentage.message') }]}
                    >
                      <Slider
                        min={1}
                        max={200}
                        marks={{
                          1: '1%',
                          50: '50%',
                          100: '100%',
                          200: '200%'
                        }}
                        tipFormatter={value => `${value}%`}
                      />
                    </Form.Item>
                  )}
                  
                  <Form.Item label={t('resize.output.format')}>
                    <Select 
                      value={targetFormat} 
                      onChange={setTargetFormat}
                      style={{ width: '100%' }}
                    >
                      <Option value="original">{t('resize.format.original')}</Option>
                      <Option value="jpg">JPG</Option>
                      <Option value="png">PNG</Option>
                      <Option value="webp">WebP</Option>
                    </Select>
                  </Form.Item>
                </Space>
              </Form>
              
              {isResizing ? (
                <div style={{ marginTop: 24 }}>
                  <Progress percent={progressPercent} status="active" />
                  <p style={{ textAlign: 'center', marginTop: 8 }}>
                    {progressPercent < 100 
                      ? t('resize.status.processing') 
                      : t('resize.status.complete')}
                  </p>
                </div>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleResize}
                  disabled={!imageFile}
                  style={{ 
                    marginTop: 16,
                    backgroundColor: pageColor,
                    borderColor: pageColor
                  }}
                  icon={<FullscreenOutlined />}
                >
                  {t('resize.download.button')}
                </Button>
              )}
            </Card>
          )}
        </Col>
        
        <Col xs={24} lg={12}>
          {imageFile && (
            <Card title={t('resize.original.image')}>
              <div style={{ textAlign: 'center' }}>
                {imageFile && (
                  <>
                    <img
                      src={imageFile ? URL.createObjectURL(imageFile) : ''}
                      alt="Original"
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                      onError={(e) => {
                        console.error(t('common.image.load.failed'));
                        e.currentTarget.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                      }}
                    />
                    <Paragraph style={{ marginTop: 16 }}>
                      {originalDimensions && (
                        <>
                          {t('common.dimensions')} {formatDimensions(originalDimensions.width, originalDimensions.height)}<br />
                          {t('common.file.size')} {(imageFile.size / 1024).toFixed(2)} KB
                        </>
                      )}
                    </Paragraph>
                  </>
                )}
              </div>
            </Card>
          )}
          
          {resizedImage && (
            <Card title={t('resize.resized.image')} style={{ marginTop: 24 }}>
              <div style={{ textAlign: 'center' }}>
                <img
                  src={resizedImage}
                  alt="Resized"
                  style={{ maxWidth: '100%', maxHeight: '300px' }}
                />
                <Paragraph style={{ marginTop: 16 }}>
                  {/* Calculate size of base64 data */}
                  {t('resize.estimated.size').replace('{size}', ((resizedImage.length * 3/4) / 1024).toFixed(2))}
                </Paragraph>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadResizedImage(resizedImage)}
                  style={{ 
                    backgroundColor: pageColor,
                    borderColor: pageColor
                  }}
                >
                  {t('resize.download.resized')}
                </Button>
              </div>
            </Card>
          )}
        </Col>
      </Row>
      
      <Modal
        title={<span style={{ color: pageColor }}>{t('resize.success.title')}</span>}
        open={showSuccessModal}
        onCancel={() => setShowSuccessModal(false)}
        footer={[
          <Button key="back" onClick={() => setShowSuccessModal(false)}>
            {t('common.close')}
          </Button>,
          <Button 
            key="download" 
            type="primary" 
            icon={<DownloadOutlined />}
            style={{ 
              backgroundColor: pageColor,
              borderColor: pageColor
            }}
            onClick={() => {
              if (resizedImage) {
                downloadResizedImage(resizedImage);
              }
              setShowSuccessModal(false);
            }}>
            {t('common.download')}
          </Button>
        ]}
      >
        <p>{t('resize.success.message')}</p>
        <p>{t('resize.download.hint')}</p>
        <p>{t('resize.preview.hint')}</p>
      </Modal>
    </div>
  );
};

export default ResizePage; 